/**
 * Fixtures de l'espace « Mes factures » (mode `VITE_USE_MOCK_FACTURES=true`).
 *
 * Permet de développer/valider le front sans le module api-metier
 * `energyinvoice` (DEV-712). Le payload mock est STRICTEMENT le même que le
 * réel (`FacturesListResponse`) : le front ne bouge pas au branchement.
 *
 * Couvre les 3 états de visibilité (cf. SPEC §11.4) pour exercer le rendu :
 * `visible`, `a_rattacher` (masqué + rattacher), `a_ajouter` (masqué + ajouter).
 */

import type {
    FactureItem,
    FacturesAgregats,
    FacturesListResponse,
    FacturesQuery,
} from "@modules/courtage/types/facture-espace"

const ITEMS: FactureItem[] = [
    {
        id: "inv_0001",
        visibilite: "visible",
        pdl: "30001210000981",
        compteur_label: "Resto U Capitole",
        numero: "FAC-2025-0042",
        periode: ["2025-01-01", "2025-01-31"],
        montant_ht: 1234.56,
        montant_ttc: 1481.47,
        conso_kwh: 5400,
        fournisseur: "EDF",
        source: "chorus_pro",
        pdf_disponible: true,
    },
    {
        id: "inv_0002",
        visibilite: "visible",
        pdl: "30001210000112",
        compteur_label: "Cité U Arsenal",
        numero: "FAC-2025-0058",
        periode: ["2025-01-01", "2025-01-31"],
        montant_ht: 2711.67,
        montant_ttc: 3254.0,
        conso_kwh: 12300,
        fournisseur: "TotalEnergies",
        source: "depot_manuel",
        pdf_disponible: true,
    },
    {
        id: "inv_0003",
        visibilite: "visible",
        pdl: "30001210000981",
        compteur_label: "Resto U Capitole",
        numero: "FAC-2024-0991",
        periode: ["2024-12-01", "2024-12-31"],
        montant_ht: 1180.2,
        montant_ttc: 1416.24,
        conso_kwh: 5120,
        fournisseur: "EDF",
        source: "chorus_pro",
        pdf_disponible: true,
    },
    {
        id: "inv_0004",
        visibilite: "a_rattacher",
        pdl: "30001299000334",
        compteur_label: "Gymnase universitaire",
        numero: null,
        periode: ["2025-02-01", "2025-02-28"],
        montant_ht: null,
        montant_ttc: null,
        conso_kwh: null,
        fournisseur: "TotalEnergies",
        source: "chorus_pro",
        pdf_disponible: false,
        raison: "compteur présent sur Homeys mais non détenu par votre organisation",
        cta: {
            type: "rattacher_source",
            label: "Rattacher ce compteur",
        },
    },
    {
        id: "inv_0005",
        visibilite: "a_ajouter",
        pdl: "30001388000776",
        compteur_label: null,
        numero: null,
        periode: ["2025-03-01", "2025-03-31"],
        montant_ht: null,
        montant_ttc: null,
        conso_kwh: null,
        fournisseur: "Gaz de Bordeaux",
        source: "chorus_pro",
        pdf_disponible: false,
        raison: "compteur inconnu de Homeys",
        adresse_facture: "12 rue de la Paix, 33000 Bordeaux",
        cta: {
            type: "ajouter_source",
            label: "Ajouter ce compteur",
        },
    },
    {
        id: "inv_0006",
        visibilite: "a_ajouter",
        pdl: "30001388000801",
        compteur_label: null,
        numero: null,
        periode: ["2025-03-01", "2025-03-31"],
        montant_ht: null,
        montant_ttc: null,
        conso_kwh: null,
        fournisseur: "Ekwateur",
        source: "chorus_pro",
        pdf_disponible: false,
        raison: "compteur inconnu de Homeys",
        adresse_facture: "5 allée des Tilleuls, 33400 Talence",
        cta: {
            type: "ajouter_source",
            label: "Ajouter ce compteur",
        },
    },
]

/** Normalise pour une comparaison insensible à la casse ET aux accents. */
function normalize(s: string): string {
    return s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
}

/** Date de référence d'une facture (fin de période, sinon début). */
function refDate(f: FactureItem): string | null {
    return f.periode[1] ?? f.periode[0]
}

/** Concatène les champs cherchés par la recherche plein-texte (cf. DEV-799). */
function haystack(f: FactureItem): string {
    return [f.numero, f.pdl, f.compteur_label, f.fournisseur, f.adresse_facture]
        .filter((v): v is string => Boolean(v))
        .join(" ")
}

function computeAgregats(items: FactureItem[]): FacturesAgregats {
    const visibles = items.filter((f) => f.visibilite === "visible")
    const debuts = items
        .map((f) => f.periode[0])
        .filter((d): d is string => Boolean(d))
        .sort()
    const fins = items
        .map((f) => f.periode[1])
        .filter((d): d is string => Boolean(d))
        .sort()
    const totalHt = visibles.reduce((acc, f) => acc + (f.montant_ht ?? 0), 0)
    return {
        n_total: items.length,
        n_visible: visibles.length,
        n_a_rattacher: items.filter((f) => f.visibilite === "a_rattacher")
            .length,
        n_a_ajouter: items.filter((f) => f.visibilite === "a_ajouter").length,
        fournisseurs: Array.from(
            new Set(
                items
                    .map((f) => f.fournisseur)
                    .filter((n): n is string => Boolean(n))
            )
        ).sort(),
        periode: [debuts[0] ?? null, fins[fins.length - 1] ?? null],
        total_ht_visible: visibles.length
            ? Math.round(totalHt * 100) / 100
            : null,
    }
}

/**
 * Reproduit la réponse `GET /v5/energyinvoices` en mémoire (mêmes query params
 * que DEV-799). Honore `pdl`, `q` (plein-texte insensible casse/accents),
 * `fournisseur` (nom), `depuis`/`jusqua` (bornes période, l'« année » mappe
 * dessus), `statut` (visibilité), et `sort`/`order` (défaut `date` desc).
 *
 * Les agrégats sont calculés sur le filtre HORS facette `statut` (comportement
 * faceté standard) : les compteurs par statut restent utiles quand on n'a pas
 * encore choisi de statut, et ne s'effondrent pas à 0 quand on en sélectionne
 * un. La liste renvoyée, elle, applique bien `statut`. → à aligner côté serveur
 * (DEV-799). La forme du payload reste identique au réel.
 */
export function facturesEspaceMock(
    query: FacturesQuery = {}
): FacturesListResponse {
    // 1. Filtres « de périmètre » (tout sauf la facette statut).
    let base = ITEMS
    if (query.pdl) base = base.filter((f) => f.pdl === query.pdl)
    if (query.fournisseur) {
        base = base.filter((f) => f.fournisseur === query.fournisseur)
    }
    if (query.q) {
        const needle = normalize(query.q.trim())
        if (needle) {
            base = base.filter((f) => normalize(haystack(f)).includes(needle))
        }
    }
    if (query.depuis) {
        base = base.filter((f) => {
            const d = refDate(f)
            return d !== null && d >= query.depuis!
        })
    }
    if (query.jusqua) {
        base = base.filter((f) => {
            const d = refDate(f)
            return d !== null && d <= query.jusqua!
        })
    }

    // 2. Agrégats = compteurs par statut + options, sur le périmètre (pas la page).
    const agregats = computeAgregats(base)

    // 3. Facette statut appliquée à la liste affichée.
    let items = base
    if (query.statut) {
        items = items.filter((f) => f.visibilite === query.statut)
    }

    // 4. Tri (défaut date desc) — sur la date de référence de la période.
    const order = query.order ?? "desc"
    items = [...items].sort((a, b) => {
        const da = refDate(a) ?? ""
        const db = refDate(b) ?? ""
        const cmp = da < db ? -1 : da > db ? 1 : 0
        return order === "asc" ? cmp : -cmp
    })

    return {
        factures: items,
        next_cursor: null,
        agregats,
    }
}

export function factureEspaceMockById(id: string): FactureItem | null {
    return ITEMS.find((f) => f.id === id) ?? null
}
